import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react';
import themeConfig from './themeConfig.json';

const varRoot = ':host';

const tagElementSizing = {
  gap: '1',
  '--tag-element-size': 'spacing.4',
  '--tag-element-offset': '0px',
};

export const config = defineConfig({
  theme: {
    tokens: {
      fonts: {
        heading: { value: themeConfig['type-primary-font'] },
        body: { value: themeConfig['type-primary-font'] },
      },
      colors: {
        primary: {
          50: { value: themeConfig['color-primary-50'] },
          100: { value: themeConfig['color-primary-100'] },
          200: { value: themeConfig['color-primary-200'] },
          300: { value: themeConfig['color-primary-300'] },
          400: { value: themeConfig['color-primary-400'] },
          500: { value: themeConfig['color-primary-500'] },
          600: { value: themeConfig['color-primary-600'] },
          700: { value: themeConfig['color-primary-700'] },
          800: { value: themeConfig['color-primary-800'] },
          900: { value: themeConfig['color-primary-900'] },
          950: { value: themeConfig['color-primary-950'] },
        },
      },
    },
    semanticTokens: {
      colors: {
        primary: {
          solid: { value: themeConfig['color-primary'] },
          contrast: { value: themeConfig['color-brand-contrast'] },
          fg: { value: themeConfig['color-primary'] },
          muted: { value: '{colors.primary.500}' },
          subtle: { value: '{colors.primary.50}' },
          emphasized: { value: '{colors.primary.500}' },
          focusRing: { value: '{colors.primary.200}' },
        },
        input: {
          bg: {
            subtle: { value: '{colors.bg.muted}' },
          },
        },
      },
    },
    slotRecipes: {
      table: defineSlotRecipe({
        slots: ['root', 'header', 'body', 'row', 'cell', 'columnHeader', 'footer', 'caption'],
        base: {
          header: {
            backgroundColor: 'bg.muted',
          },
          columnHeader: {
            backgroundColor: 'bg.muted',
          },
          cell: {
            color: 'fg',
          },
        },
      }),
      dataGrid: defineSlotRecipe({
        className: 'v-data-grid',
        slots: ['root', 'header', 'body', 'row', 'cell', 'columnHeader', 'footer', 'caption'],
        base: {
          root: {
            fontVariantNumeric: 'lining-nums tabular-nums',
            borderCollapse: 'collapse',
            width: 'full',
            textAlign: 'start',
            verticalAlign: 'top',
          },
          row: {
            _selected: {
              bg: 'colorPalette.subtle',
            },
          },
          cell: {
            textAlign: 'start',
            alignItems: 'center',
            bg: 'bg',
          },
          columnHeader: {
            fontWeight: 'medium',
            textAlign: 'start',
            color: 'fg',
          },
          caption: {
            fontWeight: 'medium',
            textStyle: 'xs',
          },
          footer: {
            fontWeight: 'medium',
          },
        },

        variants: {
          interactive: {
            true: {
              body: {
                '& tr': {
                  _hover: {
                    bg: 'colorPalette.subtle',
                  },
                },
              },
            },
          },

          stickyHeader: {
            true: {
              header: {
                '& :where(tr)': {
                  top: 'var(--table-sticky-offset, 0)',
                  position: 'sticky',
                  zIndex: 1,
                },
              },
            },
          },

          striped: {
            true: {
              row: {
                '&:nth-of-type(odd) td': {
                  bg: 'bg.muted',
                },
              },
            },
          },

          showColumnBorder: {
            true: {
              columnHeader: {
                '&:not(:last-of-type)': {
                  borderInlineEndWidth: '1px',
                },
              },
              cell: {
                '&:not(:last-of-type)': {
                  borderInlineEndWidth: '1px',
                },
              },
            },
          },

          pending: {
            true: {
              cell: {
                bg: 'var(--yellow-subtle, #FEF9C3)',
                color: 'var(--yellow-fg, #713F12)',
                borderBottom: '1px solid var(--border-default, #E4E4E7)',
              },
            },
          },

          variant: {
            line: {
              row: {
                bg: 'bg',
                borderBottomWidth: '1px',
              },
            },

            outline: {
              root: {
                boxShadow: '0 0 0 1px {colors.border}',
              },
              columnHeader: {
                borderBottomWidth: '1px',
              },
              header: {
                bg: 'bg.muted',
              },
              row: {
                '&:not(:last-of-type)': {
                  borderBottomWidth: '1px',
                },
              },
              footer: {
                borderTopWidth: '1px',
              },
            },
          },

          size: {
            sm: {
              root: {
                textStyle: 'sm',
              },
              columnHeader: {
                px: '2',
                py: '2',
              },
              cell: {
                px: '2',
                py: '2',
              },
            },

            md: {
              root: {
                textStyle: 'sm',
              },
              columnHeader: {
                px: '3',
                py: '3',
              },
              cell: {
                px: '3',
                py: '3',
              },
            },

            lg: {
              root: {
                textStyle: 'md',
              },
              columnHeader: {
                px: '4',
                py: '3',
              },
              cell: {
                px: '4',
                py: '3',
              },
            },
          },
        },

        defaultVariants: {
          variant: 'line',
          size: 'md',
        },
      }),
      tag: defineSlotRecipe({
        base: {
          root: {
            borderRadius: 'md',
          },
          closeTrigger: {
            color: 'fg.subtle',
          },
        },
        variants: {
          variant: {
            outline: {
              root: {
                bg: 'bg',
                color: 'fg',
              },
            },
            subtle: {
              root: {
                bg: 'bg.muted',
                color: 'fg',
              },
            },
            solid: {
              root: {
                bg: 'primary.subtle',
                color: 'primary.fg',
              },
              closeTrigger: {
                color: 'primary.fg',
              },
            },
          },
          size: {
            sm: { root: { px: '2', py: '1', ...tagElementSizing } },
            md: { root: { px: '2', py: '1', ...tagElementSizing }, label: { textStyle: 'sm' } },
            lg: { root: { px: '2.5', py: '1.5', ...tagElementSizing } },
            xl: { root: { py: '1.5', ...tagElementSizing }, label: { textStyle: 'md' } },
          },
        },
      }),
      breadcrumb: defineSlotRecipe({
        base: {
          list: {
            color: 'fg',
          },
          link: {
            borderRadius: 'l2',
            '&[href]': {
              _hover: {
                textDecoration: 'underline',
              },
            },
          },
          separator: {
            color: 'fg',
            _icon: {
              boxSize: '4',
            },
          },
        },
        variants: {
          variant: {
            plain: {
              link: {
                color: 'fg',
                _hover: { color: 'fg' },
              },
            },
          },
          size: {
            sm: {
              list: { fontWeight: 'medium' },
              link: { height: '8', px: '2.5', _currentPage: { fontWeight: 'semibold' } },
            },
            md: {
              list: { gap: '1', fontWeight: 'semibold' },
              link: { height: '9', px: '3.5', _currentPage: { fontWeight: 'bold' } },
            },
            lg: {
              list: { textStyle: 'sm', fontWeight: 'semibold' },
              link: { height: '10', px: '4', _currentPage: { fontWeight: 'bold' } },
            },
          },
        },
      }),
      combobox: defineSlotRecipe({
        variants: {
          variant: {
            outline: {
              control: {
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: '1',
                minH: 'var(--combobox-input-height)',
                ps: '1.5',
                pe: 'var(--combobox-input-padding-end)',
                py: '1',
                borderWidth: '1px',
                borderRadius: 'sm',
                bg: 'bg.panel',
                _focusWithin: {
                  borderColor: 'colorPalette.focusRing',
                },
                _invalid: {
                  borderColor: 'border.error',
                },
                _disabled: {
                  layerStyle: 'disabled',
                },
              },
              input: {
                flex: '1',
                width: 'auto',
                minW: '24',
                minH: 'calc(var(--combobox-input-height) - {spacing.2} - 2px)',
                px: '1.5',
                py: '0',
                borderWidth: '0',
                bg: 'transparent',
                focusVisibleRing: 'none',
                _disabled: {
                  opacity: '1',
                },
              },
            },
          },
          size: {
            xs: {
              indicatorGroup: {
                '--combobox-indicator-size': 'sizes.4',
              },
            },
          },
        },
      }),
    },
    recipes: {
      button: defineRecipe({
        base: {
          fontWeight: 'bold',
          colorPalette: 'primary',
        },
        variants: {
          variant: {
            solid: {
              backgroundColor: 'colorPalette.solid',
            },
            outline: {
              backgroundColor: 'white',
              _hover: {
                backgroundColor: 'colorPalette.100',
              },
            },
          },
        },
      }),
      input: defineRecipe({
        base: {
          borderRadius: 'md',
          _disabled: { bg: 'bg.muted' },
        },
        variants: {
          variant: {
            outline: {
              bg: 'white',
            },
          },
          size: {
            xs: {
              height: '8',
              textStyle: 'sm',
              px: '2',
            },
          },
        },
      }),
      textarea: defineRecipe({
        base: {
          _disabled: { bg: 'bg.muted' },
        },
        variants: {
          variant: {
            outline: {
              bg: 'white',
            },
          },
        },
      }),
      badge: defineRecipe({
        variants: {
          variant: {
            primary: {
              backgroundColor: 'primary.900',
              color: 'white',
            },
          },
        },
      }),
    },
  },
  cssVarsRoot: varRoot,
  conditions: {
    light: `${varRoot} &, .light &`,
  },
  preflight: { scope: varRoot },
  globalCss: {
    [varRoot]: { ...(defaultConfig.globalCss?.html ?? {}), color: 'var(--color-foreground)' },
    '*': {
      // TODO works but should be using theme tokens instead
      // '--chakra-colors-color-palette-solid': '#173E5C !important',
      '--chakra-colors-primary-contrast': 'var(--color-brand-contrast, #FFF) !important',
      fontFamily: 'var(--type-primary-font)',
      textTransform: 'var(--type-body-transform, none)',
    },
    '.ddButton.hasChakraButtonFeature': {
      display: 'flex',
      gap: 'var(--margin)',
      justifyContent: 'space-between',
    },
    '.chakra-button .icon': {
      height: 'var(--type-button-line-height)',
      margin: '0 calc(var(--margin) / 4) 0 0',
      verticalAlign: 'middle',
    },
    '.button_content': {
      display: 'flex',
      gap: 'var(--margin)',
      justifyContent: 'space-between',
    },
    '[data-scope="date-picker"][data-part="control"]': {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    '[data-scope="date-picker"][data-part="trigger"]': {
      position: 'absolute',
      right: '8px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0',
      fontSize: '14px',
      lineHeight: '1',
      display: 'inline-flex',
      alignItems: 'center',
      color: 'var(--chakra-colors-fg-muted)',
    },
    '[data-scope="date-picker"][data-part="positioner"]': {
      zIndex: '2000 !important',
    },
    '[data-scope="date-picker"][data-part="content"]': {
      background: 'var(--chakra-colors-bg-panel)',
      borderRadius: 'var(--chakra-radii-l3)',
      boxShadow: 'var(--chakra-shadows-lg)',
      padding: '12px',
      outline: '0',
    },
    '[data-scope="date-picker"][data-part="view-control"]': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '8px',
      gap: '4px',
    },
    '[data-scope="date-picker"][data-part="prev-trigger"], [data-scope="date-picker"][data-part="next-trigger"]':
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: 'var(--chakra-radii-md)',
        color: 'var(--chakra-colors-fg-muted)',
        width: '32px',
        height: '32px',
        '&:hover': { background: 'var(--chakra-colors-bg-muted)' },
      },
    '[data-scope="date-picker"][data-part="view-trigger"]': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '1',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '14px',
      padding: '4px 8px',
      borderRadius: 'var(--chakra-radii-md)',
      '&:hover': { background: 'var(--chakra-colors-bg-muted)' },
    },
    '[data-scope="date-picker"][data-part="table"]': {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '0',
    },
    '[data-scope="date-picker"][data-part="table-header"]': {
      padding: '4px',
      textAlign: 'center',
      fontSize: '12px',
      fontWeight: '500',
      color: 'var(--chakra-colors-fg-muted)',
      height: '32px',
    },
    '[data-scope="date-picker"][data-part="table-cell-trigger"]': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: 'var(--chakra-radii-md)',
      border: 'none',
      cursor: 'pointer',
      fontSize: '13px',
      background: 'transparent',
      '&:hover': { background: 'var(--chakra-colors-bg-muted)' },
      '&[data-selected]': {
        background: 'var(--color-brand)',
        color: 'var(--chakra-colors-color-palette-contrast)',
      },
      '&[data-today]:not([data-selected])': {
        outline: '1px solid var(--chakra-colors-border)',
      },
      '&[data-outside-range]': {
        color: 'var(--chakra-colors-fg-subtle)',
      },
      '&[data-disabled]': {
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
    '[data-scope="date-picker"][data-part="table-cell-trigger"][data-view="month"], [data-scope="date-picker"][data-part="table-cell-trigger"][data-view="year"]':
      {
        width: '100%',
        padding: '6px 12px',
      },
  },
});

export const system = createSystem(defaultConfig, config);
